import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

import RideFormDialog from './RideFormDialog';

const RideDialogContext = createContext(null);

/**
 * Globaler Provider für den Fahrten-Dialog (Neu/Bearbeiten/Zuweisen).
 * Einmalig weit oben im authentifizierten Baum (App.js) gemountet.
 *
 * useRideDialog() liefert:
 *   openRideDialog({ mode, rideId, draft })
 *     - mode:   'create' | 'edit' | 'assign'
 *     - rideId: nur bei edit/assign
 *     - draft:  optionaler Entwurf ({ duplicateRide } oder { incomingCallDraft })
 *   closeRideDialog()
 */
export const RideDialogProvider = ({ children }) => {
  const [state, setState] = useState({
    open: false,
    mode: 'create',
    rideId: null,
    draft: null,
    key: 0,
  });

  const openRideDialog = useCallback(({ mode = 'create', rideId = null, draft = null } = {}) => {
    // key hochzählen → RideFormDialog remountet RideForm frisch.
    setState((s) => ({ open: true, mode, rideId, draft, key: s.key + 1 }));
  }, []);

  const closeRideDialog = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const value = useMemo(
    () => ({ openRideDialog, closeRideDialog }),
    [openRideDialog, closeRideDialog]
  );

  return (
    <RideDialogContext.Provider value={value}>
      {children}
      <RideFormDialog
        open={state.open}
        mode={state.mode}
        rideId={state.rideId}
        draft={state.draft}
        dialogKey={state.key}
        onClose={closeRideDialog}
        onSuccess={closeRideDialog}
      />
    </RideDialogContext.Provider>
  );
};

/**
 * Hook für den Zugriff auf den Fahrten-Dialog. Gibt null zurück außerhalb des Providers.
 */
export const useRideDialog = () => useContext(RideDialogContext);

export default RideDialogProvider;
